import React, { useRef, useImperativeHandle, useState } from "react";
import { Form } from "@wellth/web-ui";
import { Moment } from "moment-timezone";
import {
  dayIsInThePast,
  dayIsInTheFuture,
  dayIsTodayOrInThePast,
} from "utils/date";
import { CheckInCategory, ReminderInterval, Maybe } from "types/globalTypes";
import { AdminPrivilege } from "constants/adminPrivilege";
import { programTemplatesNotAllowingMedReminders } from "components/EnrollmentForm/EnrollmentPolicy";
import { reminderStateChangeHandler } from "components/EditReminderForm/EditReminderPolicy";
import { programSpecificReminderConfigs } from "components/EnrollmentForm/sections/reminders/reminderConfig/config";
import inputs, { ReminderMethod } from "./inputs";
import Footer from "./components/Footer";

const shouldDisableReminderDeleteButton = ({
  startMoment,
  timezone,
  endMoment,
}: {
  startMoment: Moment;
  timezone: string;
  endMoment?: Moment;
}): boolean => {
  const startDate = startMoment.toDate();
  const startDatePassed = dayIsInThePast(startDate, timezone);

  // if no endDate and reminder is active
  if (!endMoment && startDatePassed) return false;

  const endDate = endMoment.toDate();
  return (
    (startDatePassed && dayIsTodayOrInThePast(endDate, timezone)) ||
    (startDatePassed && dayIsInTheFuture(endDate, timezone))
  );
};

const formatIsDemo = (isDemo: boolean): string => (isDemo ? "True" : "False");
const formatEndDate = (endDate: Moment | null): string =>
  endDate ? endDate.format("L") : "";

export interface ReminderProps {
  id: string;
  dueTime: Moment;
  checkInCategory: CheckInCategory;
  interval: ReminderInterval;
  startDate: Moment;
  endDate: Maybe<Moment>;
  windowBeforeMinutes: number;
  windowAfterMinutes: number;
  isDemo: boolean;
  isRequired: boolean;
  label?: string;
}

export interface EditReminderFormProps extends Partial<ReminderProps> {
  method: ReminderMethod;
  deleteReminder: (reminderId: string) => void;
  timezone: string;
  deleteLoading?: boolean;
  adminRole: AdminPrivilege;
  programTemplate: string;
  programCode: string;
}

const EditReminderForm: React.RefForwardingComponent<
  any,
  EditReminderFormProps
> = (
  {
    method,
    timezone,
    deleteReminder,
    id,
    dueTime,
    checkInCategory,
    interval,
    startDate,
    endDate,
    windowAfterMinutes = 180,
    windowBeforeMinutes = 360,
    isDemo,
    isRequired,
    deleteLoading = false,
    adminRole,
    programTemplate,
    programCode,
    label,
  }: EditReminderFormProps,
  ref,
) => {
  const formRef = useRef(null);
  const [isRequiredOverride, setIsRequiredOverride] = useState(
    programTemplatesNotAllowingMedReminders.includes(programTemplate) &&
      checkInCategory === CheckInCategory.Medication,
  );

  // Calculate the default isRequired value based on the actual reminder configuration
  const checkIfDefaultRequired = () => {
    if (method !== ReminderMethod.Create) {
      return isRequired ?? true; // Fallback to true if isRequired is undefined
    }
    
    // Check if programCode exists in programSpecificReminderConfigs
    const hasProgramSpecificConfig = programCode && programSpecificReminderConfigs[programCode];
    
    // If program has specific config, always default to true
    if (hasProgramSpecificConfig) {
      return true;
    }
    
    // Only default to false when:
    // 1. programCode is NOT found in programSpecificReminderConfigs (falls back to programTemplate)
    // 2. AND programTemplate is "MA - Payer" or "MA - DSNP"
    if (programTemplatesNotAllowingMedReminders.includes(programTemplate)) {
      return false;
    }
    
    // For all other configurations, default to true
    return true;
  };

  const defaultIsRequired = checkIfDefaultRequired();

  const [formState, setFormState] = useState({
    checkInCategory,
    windowBeforeMinutes,
    windowAfterMinutes,
    isDemo,
    interval,
    startDate,
    endDate,
    dueTime,
    timezone,
    isRequired: method === ReminderMethod.Create ? defaultIsRequired : (isRequired ?? true),
    label,
  });

  const isReviewing = method === ReminderMethod.Info;
  const isEditing = method === ReminderMethod.Edit;

  useImperativeHandle(ref, () => ({
    getReminder: () =>
      new Promise((resolve, reject) =>
        formRef.current.props.form.validateFields((errors, values) =>
          errors ? reject(errors) : resolve(values),
        ),
      ),
  }));

  let timeoutId;
  function handleValueChange(changedValues) {
    const newState = reminderStateChangeHandler(
      formState,
      changedValues,
      programTemplate,
      setIsRequiredOverride,
    );


    if (Object.keys(changedValues).includes("label")) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setFormState(newState);
        timeoutId = null;
      }, 1000);
    } else {
      setFormState(newState);
    }
  }

  const isCue = formState.checkInCategory === CheckInCategory.Cue;
  const labelEdit = isEditing; // Allow free-form editing only when editing existing reminders

  return (
    <>
      <Form
        wrappedComponentRef={formRef}
        onValuesChange={handleValueChange}
        mapPropsToFields={(_props, createFormField) => ({
          checkInCategory: createFormField({
            value: formState.checkInCategory,
          }),
          interval: createFormField({ value: formState.interval }),
          dueTime: createFormField({
            value: isReviewing
              ? formState.dueTime.format("LT")
              : formState.dueTime,
          }),
          startDate: createFormField({
            value: isReviewing
              ? formState.startDate.format("L")
              : formState.startDate,
          }),
          endDate: createFormField({
            value: isReviewing
              ? formatEndDate(formState.endDate)
              : formState.endDate,
          }),
          windowBeforeMinutes: createFormField({
            value: formState.windowBeforeMinutes,
          }),
          windowAfterMinutes: createFormField({
            value: formState.windowAfterMinutes,
          }),
          isRequired: createFormField({
            value: (() => {
              // Business Logic for isRequired field display:
              // 
              // The isRequired field represents whether a reminder is "incentivized" (required for program compliance).
              // Certain program templates (MA-Payer, MA-DSNP) don't allow medication reminders to be incentivized.
              // 
              // When isRequiredOverride is true, it means the current program template doesn't allow 
              // medication reminders to be incentivized, so we force the toggle to false.
              //
              // Context-specific behavior:
              // - REVIEWING: Show the actual database value - no business rules applied
              // - CREATING: Apply business rules - set to false if medication incentive not allowed for this program template
              // - EDITING: Show the actual database value - user can see and modify what was previously set
              const value = isReviewing
                ? formState.isRequired
                : method === ReminderMethod.Create 
                  ? formState.isRequired && !isRequiredOverride
                  : formState.isRequired; // In edit mode, always use the actual form state value
              
              return value;
            })(),
          }),
          isDemo: createFormField({
            value: isReviewing
              ? formatIsDemo(formState.isDemo)
              : formState.isDemo,
          }),
          label: createFormField({
            value: formState.label,
          }),
        })}
        inputs={inputs(
          isEditing
            ? dayIsInThePast(formState.startDate.toDate(), formState.timezone)
            : false,
          formState.timezone,
          method,
          isRequiredOverride,
          adminRole,
          programCode,
          isCue,
          labelEdit,
        )}
        labelCol={{
          span: 7,
        }}
        wrapperCol={{
          xs: { span: 24 },
          sm: { span: 12 },
        }}
        colValue={12}
      />
      {isEditing ? (
        <Footer
          adminRole={adminRole}
          deleteReminder={() => {
            deleteReminder(id);
          }}
          disabled={shouldDisableReminderDeleteButton({
            startMoment: formState.startDate,
            timezone: formState.timezone,
            endMoment: formState.endDate,
          })}
          loading={deleteLoading}
        />
      ) : null}
    </>
  );
};

export default React.forwardRef(EditReminderForm);
